// Token-budget enforcement for the Context Pack Generator (ARCHITECTURE.md §9.3).
//
// Ranked files are accumulated in score order until the budget is exhausted.
// 10% of the budget is reserved for metadata (artifact definitions, dependency
// context), so files compete for the remaining 90%. Directly-changed files
// (depth 0) are always included even if they overflow the budget; everything
// else is dropped once the running total would exceed the file budget.

import type { RankedFile } from "./ranker.js";

// ---------------------------------------------------------------------------
// Token estimation
// ---------------------------------------------------------------------------

/**
 * Rough token estimate: ~4 characters per token. Deterministic and cheap; good
 * enough for budget accounting without a real tokenizer.
 */
export function estimateTokens(content: string): number {
  return Math.ceil(content.length / 4);
}

/** Fraction of the budget reserved for metadata entries (§9.3). */
export const METADATA_RESERVE_FRACTION = 0.1;

// ---------------------------------------------------------------------------
// BudgetSelection
// ---------------------------------------------------------------------------

export interface SelectedFile {
  path: string;
  relevanceScore: number;
  tokens: number;
}

export interface BudgetSelection {
  selected: SelectedFile[];
  /** Paths dropped because they would exceed the file budget. */
  excluded: string[];
  /** Total tokens of the selected files (may exceed budget via depth-0 files). */
  totalTokens: number;
}

export interface SelectWithinBudgetOptions {
  ranked: RankedFile[];
  budgetTokens: number;
  /** filePath → content; missing entries are treated as empty (0 tokens). */
  fileContents: Map<string, string>;
}

/**
 * Select files to include within the token budget (ARCHITECTURE.md §9.3).
 * Iterates in rank order, always including depth-0 files and admitting the
 * rest only while they fit under the metadata-reserved file budget.
 */
export function selectWithinBudget(
  options: SelectWithinBudgetOptions,
): BudgetSelection {
  const { ranked, budgetTokens, fileContents } = options;

  const fileBudget = budgetTokens * (1 - METADATA_RESERVE_FRACTION);

  const selected: SelectedFile[] = [];
  const excluded: string[] = [];
  let totalTokens = 0;

  for (const file of ranked) {
    const content = fileContents.get(file.path) ?? "";
    const tokens = estimateTokens(content);

    if (file.depth === 0) {
      // Directly-changed files are mandatory; allow budget overflow.
      selected.push({ path: file.path, relevanceScore: file.relevanceScore, tokens });
      totalTokens += tokens;
      continue;
    }

    if (totalTokens + tokens <= fileBudget) {
      selected.push({ path: file.path, relevanceScore: file.relevanceScore, tokens });
      totalTokens += tokens;
    } else {
      excluded.push(file.path);
    }
  }

  return { selected, excluded, totalTokens };
}
